import { useTranslation } from "next-i18next";
import Image from "next/image";

import { useFormatPrice } from "@/common/hooks/intl.hooks";
import { ValueCheckPhotoButton } from "@/features/value-check/components/ValueCheckPhotoButton";
import { ValueCheckThumb } from "@/features/value-check/components/ValueCheckThumb";
import { Heading } from "@/ui/cva/Heading";
import { InfoLabel } from "@/ui/cva/InfoLabel";
import { Body } from "@/ui/typography/Body";
import { Flavor } from "@/ui/typography/Flavor";
import { Helper } from "@/ui/typography/Helper";

/** The example card's range: round figures near what a Togo-style chair sold for (27 Oct check). */
const EXAMPLE_LOW = 800;
const EXAMPLE_HIGH = 2400;

/** Mobile Body is 16/20 (Notion 09.1); desktop keeps 16/24 (WHO-6534 F18). */
const BODY_LEADING = "leading-5 md:leading-6";

/** The visitor's last answer, offered on a new visit instead of opening on it (F34). */
export type ValueCheckLastCheck = {
  photoUrl: string;
  title: string;
};

/**
 * What an answer looks like, marked as an example so nobody reads it as a promise (WHO-6534 F14).
 * On desktop it fills the right column next to the intro and the upload (F28).
 */
const ValueCheckExample = () => {
  const { t } = useTranslation();
  const { formatPrice } = useFormatPrice();

  return (
    <figure className="relative min-h-[280px] overflow-hidden rounded bg-grey-100 md:min-h-[414px]">
      <Image
        src="/images/value-check/example-togo.jpg"
        alt={t(
          "value-check:example-alt",
          "Example of a result: an armchair with a price tag"
        )}
        fill
        sizes="(min-width: 768px) 560px, 100vw"
        className="object-cover"
      />
      <InfoLabel
        color="curious"
        size="small"
        corners="square"
        className="absolute left-3 top-3"
      >
        {t("value-check:example-label", "Example")}
      </InfoLabel>
      <figcaption
        aria-hidden
        className="absolute bottom-3 right-3 grid max-w-[16rem] gap-0.5 rounded border border-grey-300 bg-white px-3 py-2"
      >
        <Flavor className="text-brand-600">
          {t("value-check:price-title", "Comparable items sold for")}
        </Flavor>
        <span className="font-title text-lg font-bold tabular-nums">
          {t("value-check:price-range", "{{low}} to {{high}}", {
            low: formatPrice(EXAMPLE_LOW),
            high: formatPrice(EXAMPLE_HIGH),
          })}
        </span>
      </figcaption>
    </figure>
  );
};

/** "Your last check": the photo, what it was and a way back to the answer (F34). */
const ValueCheckLastCheckCard = ({
  lastCheck,
  onOpen,
}: {
  lastCheck: ValueCheckLastCheck;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <section
      aria-labelledby="value-check-last"
      className="flex min-w-0 items-start gap-4 rounded border border-grey-300 p-4 md:[grid-area:last]"
      data-testid="value-check.last-check"
    >
      <ValueCheckThumb photoUrl={lastCheck.photoUrl} className="size-[72px]" />
      <div className="grid min-w-0 flex-1">
        <Heading as="h2" size="h5" id="value-check-last">
          {t("value-check:last-check-title", "Your last check:")}
        </Heading>
        <Body className={`truncate text-grey-800 ${BODY_LEADING}`}>
          {lastCheck.title}
        </Body>
        <button
          type="button"
          className="justify-self-start font-body text-base text-brand-500 underline hover:text-brand-600"
          onClick={onOpen}
        >
          {t("value-check:last-check-open", "View result")}
        </button>
      </div>
    </section>
  );
};

/**
 * Before a photo: title, intro and the upload first on every width (F28), the example beside it
 * on desktop, three steps from photo to ad, a second upload at the bottom, and the line that this
 * is an indication, not a guarantee.
 */
export const ValueCheckLanding = ({
  onPick,
  lastCheck = null,
  onOpenLastCheck,
}: {
  onPick: (file: File) => void;
  lastCheck?: ValueCheckLastCheck | null;
  onOpenLastCheck?: () => void;
}) => {
  const { t } = useTranslation();

  const badges = [
    t("value-check:badge-free", "Free"),
    t("value-check:badge-no-account", "No account needed"),
    t("value-check:badge-no-strings", "No strings attached"),
  ];

  const steps = [
    {
      title: t("value-check:step-1-title", "Upload a photo"),
      body: t(
        "value-check:step-1-body",
        "We see what kind of item it is and what it's made of. We only name the brand when we're sure of it."
      ),
    },
    {
      title: t("value-check:step-2-title", "Get a price indication"),
      body: t(
        "value-check:step-2-body",
        "You see what comparable items really fetched. Then you choose your own price."
      ),
    },
    {
      title: t("value-check:step-3-title", "Your ad is ready"),
      body: t(
        "value-check:step-3-body",
        "We fill in the title, description and details from your photos. You change what you like and place it."
      ),
    },
  ];

  // One name for the one action, on both buttons (F31).
  const uploadLabel = t("value-check:upload-button", "Choose photo");

  return (
    <div className="grid gap-12 md:gap-16">
      <section
        aria-labelledby="value-check-title"
        className="grid gap-6 md:grid-cols-2 md:items-start md:gap-x-10 md:[grid-template-areas:'intro_figure''card_figure''last_figure']"
      >
        <div className="grid gap-4 md:[grid-area:intro]">
          <Heading as="h1" size="h1" id="value-check-title">
            {t("value-check:title", "What is your item worth?")}
          </Heading>
          <Body className={`max-w-[34em] text-grey-800 ${BODY_LEADING}`}>
            {t(
              "value-check:lead",
              "Upload a photo. We recognise your item and show what comparable items sold for on Whoppah."
            )}
          </Body>
        </div>
        <div className="grid gap-4 rounded bg-flavour-50 p-6 md:[grid-area:card]">
          <div className="grid gap-1">
            <Heading as="h2" size="h5">
              {t("value-check:upload-title", "Choose or take a photo")}
            </Heading>
            <Helper className="text-grey-700">
              {t(
                "value-check:upload-hint",
                "The whole item in view, in good light."
              )}
            </Helper>
          </div>
          <ValueCheckPhotoButton label={uploadLabel} onPick={onPick} />
          <ul className="flex flex-wrap gap-2">
            {badges.map(badge => (
              <li key={badge}>
                <InfoLabel color="brand" size="small" corners="rounded">
                  {badge}
                </InfoLabel>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:self-stretch md:[grid-area:figure]">
          <ValueCheckExample />
        </div>
        {lastCheck && onOpenLastCheck && (
          <ValueCheckLastCheckCard
            lastCheck={lastCheck}
            onOpen={onOpenLastCheck}
          />
        )}
      </section>

      <section aria-labelledby="value-check-how" className="grid gap-6">
        <div className="grid gap-2">
          <Heading as="h2" size="h3" id="value-check-how">
            {t("value-check:how-title", "From photo to ad")}
          </Heading>
          <Body className={`text-grey-800 ${BODY_LEADING}`}>
            {t(
              "value-check:how-lead",
              "After the check your ad is ready for you. You check it and place it."
            )}
          </Body>
        </div>
        <ol className="grid gap-4 md:grid-cols-3">
          {steps.map((step, index) => (
            <li
              key={step.title}
              className="grid content-start gap-1 rounded border border-grey-300 p-5"
            >
              <Flavor className="text-brand-600">
                {t("value-check:step-label", "Step {{number}}", {
                  number: index + 1,
                })}
              </Flavor>
              <Heading as="h3" size="h5">
                {step.title}
              </Heading>
              <Helper className="text-grey-800">{step.body}</Helper>
            </li>
          ))}
        </ol>
      </section>

      <section
        aria-labelledby="value-check-cta"
        className="flex flex-col gap-6 rounded bg-brand-500 px-4 py-8 md:flex-row md:flex-wrap md:items-center md:justify-between md:px-8 md:py-10"
      >
        <div className="grid min-w-0 gap-1">
          <Heading
            as="h2"
            size="h3"
            id="value-check-cta"
            className="text-white"
          >
            {t("value-check:cta-title", "Curious what your item will fetch?")}
          </Heading>
          <Body className={`text-white ${BODY_LEADING}`}>
            {t("value-check:cta-body", "One photo is enough to start.")}
          </Body>
        </div>
        <ValueCheckPhotoButton
          label={uploadLabel}
          onPick={onPick}
          color="ghost"
        />
      </section>

      <Helper className="max-w-[46em] text-grey-700">
        {t(
          "value-check:disclaimer",
          "The price indication is based on earlier sales on Whoppah. An indication, not a guarantee: the final price depends on the condition, your photos and buyer interest."
        )}
      </Helper>
    </div>
  );
};
